<template>
  <div :id="id" class="chapter-section panel">
    <p class="panel-heading">
      {{ $t(`chapter11.title`) }}
      <b-tooltip :label="$t(`general.showRechtstext`)" position="is-right" type="is-warning">
        <b-icon
          type="is-warning"
          icon="format-section"
          class="legal-toggle"
          :class="{ 'is-active': showLegal_c11 }"
          @click.native="showLegal_c11 = !showLegal_c11"
        />
      </b-tooltip>
    </p>

    <fieldset class="box">
      <h4 class="rechtstext-title">{{ $t(`chapter11.q0.title`) }}</h4>
      <RechtsText v-if="showLegal_c11">
        <div v-html="renderedMarkdown('rechtstexte.c11q0')" />
        <p class="multiline">
          <i18n-t keypath="rechtstexte.c11q0b" tag="span" scope="global">
            <template #link><LegalCitation citation-id="dsgvoArt9" /></template>
            <template #link2><LegalCitation citation-id="dsgvoArt89" /></template>
            <template #link3><LegalCitation citation-id="hdsig24Abs1" /></template>
            <template #link4><LegalCitation citation-id="hkhg12" /></template>
            <template #link5><LegalCitation citation-id="hdsig24Abs3Satz2" /></template>
          </i18n-t>
        </p>
        <p class="multiline">
          <i18n-t keypath="rechtstexte.c11q0c" tag="span" scope="global">
            <template #link><LegalCitation citation-id="gdng6" /></template>
            <template #link2><LegalCitation citation-id="dsgvoArt6Abs1LitA" /></template>
            <template #link3><LegalCitation citation-id="dsgvoArt9Abs2LitA" /></template>
          </i18n-t>
        </p>
      </RechtsText>

      <h4 class="rechtstext-title">{{ $t(`chapter11.q1.title`) }}</h4>
      <RechtsText v-if="showLegal_c11">
        <div v-html="renderedMarkdown('rechtstexte.c11q1')" />
      </RechtsText>

      <h4 class="rechtstext-title">{{ $t(`chapter11.q2.title`) }}</h4>
      <RechtsText v-if="showLegal_c11">
        <h5>{{ $t('rechtstexte.c11q2TransparenzTitle') }}</h5>
        <p class="multiline">
          <i18n-t keypath="rechtstexte.c11q2TransparenzText" tag="span" scope="global">
            <template #link><LegalCitation citation-id="dsgvoArt12" /></template>
          </i18n-t>
        </p>

        <h5>{{ $t('rechtstexte.c11q2InformationspflichtTitle') }}</h5>
        <p class="multiline">
          <i18n-t keypath="rechtstexte.c11q2InformationspflichtText" tag="span" scope="global">
            <template #link><LegalCitation citation-id="dsgvoArt13" /></template>
            <template #link2><LegalCitation citation-id="dsgvoArt13_14" /></template>
            <template #link3><LegalCitation citation-id="amg" /></template>
            <template #link4><LegalCitation citation-id="mpdg" /></template>
          </i18n-t>
        </p>

        <h5>{{ $t('rechtstexte.c11q2AuskunftTitle') }}</h5>
        <p class="multiline">
          <i18n-t keypath="rechtstexte.c11q2AuskunftText" tag="span" scope="global">
            <template #link><LegalCitation citation-id="dsgvoArt15" /></template>
          </i18n-t>
        </p>

        <h5>{{ $t('rechtstexte.c11q2BerichtigungTitle') }}</h5>
        <p class="multiline">{{ $t('rechtstexte.c11q2BerichtigungText') }}</p>

        <h5>{{ $t('rechtstexte.c11q2LoeschungTitle') }}</h5>
        <p class="multiline">
          <i18n-t keypath="rechtstexte.c11q2LoeschungText" tag="span" scope="global">
            <template #link><LegalCitation citation-id="dsgvoArt17" /></template>
            <template #link2><LegalCitation citation-id="amg" /></template>
          </i18n-t>
        </p>

        <h5>{{ $t('rechtstexte.c11q2EinschraenkungVerarbeitungTitle') }}</h5>
        <p class="multiline">
          <i18n-t keypath="rechtstexte.c11q2EinschraenkungVerarbeitungText" tag="span" scope="global">
            <template #link><LegalCitation citation-id="dsgvoArt18" /></template>
          </i18n-t>
        </p>

        <h5>{{ $t('rechtstexte.c11q2DatenuebertragbarkeitTitle') }}</h5>
        <p class="multiline">
          <i18n-t keypath="rechtstexte.c11q2DatenuebertragbarkeitText" tag="span" scope="global">
            <template #link><LegalCitation citation-id="dsgvoArt20" /></template>
          </i18n-t>
        </p>

        <h5>{{ $t('rechtstexte.c11q2WiderspruchsrechtTitle') }}</h5>
        <p class="multiline">
          <i18n-t keypath="rechtstexte.c11q2WiderspruchsrechtText" tag="span" scope="global">
            <template #link><LegalCitation citation-id="dsgvoArt7" /></template>
            <template #link2><LegalCitation citation-id="amg40bAbs6Satz2" /></template>
            <template #link3><LegalCitation citation-id="mpdg29Abs2" /></template>
          </i18n-t>
        </p>

        <h5>{{ $t('rechtstexte.c11q2EinschraenkungRechteTitle') }}</h5>
        <p class="multiline">
          <i18n-t keypath="rechtstexte.c11q2EinschraenkungRechteText" tag="span" scope="global">
            <template #link><LegalCitation citation-id="hdsig24Abs2" /></template>
          </i18n-t>
        </p>
      </RechtsText>
    </fieldset>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import MarkdownIt from 'markdown-it'
import { BIcon, BTooltip } from 'buefy'
import { useRechtstextToggle } from '@/composables/useVisibilityToggles'
import LegalCitation from './LegalCitation.vue'
import RechtsText from '@/components/RechtsText.vue'

defineProps<{ id: string; modelValue?: object }>()

const { t } = useI18n()

const md = new MarkdownIt({ html: false, linkify: true, typographer: true })

function renderedMarkdown(key: string): string {
  return md.render(t(key) as string)
}

const showLegal_c11 = useRechtstextToggle()
</script>

<style scoped>
.multiline {
  white-space: pre-line;
}

.rechtstext-title {
  font-size: 1.25em;
  font-weight: bold;
  margin-bottom: 0.5rem;
}

.rechtstext-title:not(:first-child) {
  margin-top: 1.5rem;
}
</style>
